import * as React from "react";
import posthog from 'posthog-js'
import HttpLeadRepository from "../api/request/Lead/Lead.service";
import { GetlifeContext } from "./GetLifeContext.context";
import TagManager from "react-gtm-module";
import { getCookie } from "../hooks/getCookie";
import { resolveTrackingAttribution } from "../utils/attributionResolve";

interface DataTrackerI {
  leadUlid?: string;
  campaign: string;
  utmSource?: string;
  utmMedium?: string;
  /** La campaña del toque cuando la fija la página (Facile native: su referencia externa). */
  utmCampaign?: string;
}

interface FsEventI {
  FsEvent: string;
  data?: {
    leadId?: string
  };
}

interface IdentifyFSI {
  lead: string;
  data: {
    email: string;
    emailDomain: string;
    brokerId: string;
  };
}
interface ContextProps {
  handleTrackerQuestion: (value: HandleTrackerI) => void;
  identifyFS: (value: IdentifyFSI) => void;
}

export const GetLifeTrackerContext = React.createContext({} as ContextProps);

interface HandleTrackerI {
  type: "FS" | "tracker" | "GA" | "trackerCompare";
  data: any;
  event?: string;
}

export const GetLifeTrackerProvider = ({ children }: any) => {
  const {
    leadId,
    utm_campaign,
    utm_source,
    utm_content,
    utm_medium,
    utm_term,
    utm_test,
    gclid,
    gbraid,
    wbraid,
    msclkid,
    fbclid,
    gtf_campaign,
    tid,
    getEntryPage,
    getReferrerUrl,
    getABTestVersion,
  } = React.useContext(GetlifeContext);

  const leadRepository = new HttpLeadRepository(leadId);

  const VWOID = getCookie("_vwo_uuid_v2");
  const GAID = getCookie("_ga");
  const AT_GD = getCookie("at_gd");
  const EXT_REFERENCE = getCookie("externalReference");

  /**
   * Atribución del contexto en memoria, como último recurso del resolutor
   * (URL > sello de sesión > cookie > este estado). Ver utils/attributionResolve.
   */
  const contextAttribution = () => ({
    utm_source,
    utm_medium,
    utm_campaign,
    utm_content,
    utm_term,
    utm_test,
    tid,
    gclid,
    gbraid,
    wbraid,
    msclkid,
    fbclid,
    entryPage: getEntryPage(),
    referrerUrl: getReferrerUrl(),
  });

  const handleChangeDataTracker = ({
    leadUlid = leadId,
    campaign,
    utmSource,
    utmMedium,
    utmCampaign
  }: DataTrackerI) => {
    try {
      // La primera fila de tracking se envía al crear el lead, casi siempre
      // antes de que el visitante toque el banner de CookieYes (que ya ha
      // borrado las cookies de atribución). El resolutor recupera el valor del
      // sello de sesión y, si la sesión empezó en /login/<token> sin ninguna
      // atribución real, marca el toque como transaccional en vez de mandar
      // una fila con todo a null que el CRM no distingue de una visita directa.
      const attribution = resolveTrackingAttribution(contextAttribution());
      leadRepository.track({
        leadUlid: leadUlid,
        googleAnalyticsId: GAID,
        fullstoryId: null,
        vwoId: VWOID,
        utmCampaign: utmCampaign ? utmCampaign : attribution.utm_campaign,
        utmSource: EXT_REFERENCE ? "facile" : utmSource ? utmSource : attribution.utm_source,
        utmMedium: EXT_REFERENCE ? "affiliate" : utmMedium ? utmMedium : attribution.utm_medium,
        utmContent: attribution.utm_content,
        utmTerm: attribution.utm_term,
        utmTest: attribution.utm_test,
        gclid: attribution.gclid,
        gbraid: attribution.gbraid,
        wbraid: attribution.wbraid,
        msclkid: attribution.msclkid,
        fbclid: attribution.fbclid,
        tid: attribution.tid,
        campaign: EXT_REFERENCE ? EXT_REFERENCE : campaign,
        atGd: AT_GD,
        entryPage: attribution.entryPage,
        referrerUrl: attribution.referrerUrl,
        aBTestVersion: getABTestVersion()
      });
    } catch (error) { }
  };

  const handleChangeDataTrackerCompare = ({
    leadUlid = leadId,
  }: DataTrackerI) => {
    try {
      const attribution = resolveTrackingAttribution(contextAttribution());
      leadRepository.trackCompare({
        leadUlid: leadUlid,
        googleAnalyticsId: GAID,
        fullstoryId: null,
        vwoId: VWOID,
        utmCampaign: attribution.utm_campaign,
        utmSource: attribution.utm_source,
        utmMedium: attribution.utm_medium,
        utmContent: attribution.utm_content,
        utmTerm: attribution.utm_term,
        utmTest: attribution.utm_test,
        gclid: attribution.gclid,
        gbraid: attribution.gbraid,
        wbraid: attribution.wbraid,
        msclkid: attribution.msclkid,
        fbclid: attribution.fbclid,
        tid: attribution.tid,
        campaign: gtf_campaign,
        atGd: AT_GD,
        entryPage: attribution.entryPage,
        referrerUrl: attribution.referrerUrl
      });
    } catch (error) { }
  };

  // FullStory was removed (2026-09-10). The "FS" tracker type is kept as the
  // product-event channel: PostHog + the internal event store. `platform`
  // stays "fullstory" on purpose: the events table is consumed downstream by
  // that literal; rename it together with the consumers.
  const sendFullStoryEvent = ({ FsEvent, data = {} }: FsEventI) => {
    if (process.env.REACT_APP_ENVIROMENT === "PRD") {
      posthog.capture(FsEvent, data);
      let dataEvent = {
        leadUlid: data.leadId,
        eventType: FsEvent,
        platform: "fullstory",
        createdAt: new Date(),
        payload: data
      }
      leadRepository.saveEvent(dataEvent)
    }
  };

  const handleChangeDataLayer = (newData: any) => {
    TagManager.dataLayer({ dataLayer: newData });
    let dataEvent = {
      leadUlid: newData.leadId,
      eventType: newData.event,
      platform: "ga4",
      createdAt: new Date(),
      payload: newData
    }
    if (process.env.REACT_APP_ENVIROMENT === "PRD" || process.env.REACT_APP_ENVIROMENT === "TEST") {
      leadRepository.saveEvent(dataEvent)
    }
  };

  const identifyFS = ({ lead, data }: IdentifyFSI) => {
    if (process.env.REACT_APP_ENVIROMENT === "PRD") {
      posthog.identify(
        lead, // Required. Replace 'distinct_id' with your user's unique identifier
        { email: data.email },  // $set, optional
        { data } // $set_once, optional
      );
    }
  };

  const handleTrackerQuestion = ({ type, event, data }: HandleTrackerI) => {
    const trackers = {
      FS: () => sendFullStoryEvent({ FsEvent: event!, data }),
      tracker: () => handleChangeDataTracker(data),
      trackerCompare: () => handleChangeDataTrackerCompare(data),
      GA: () => handleChangeDataLayer(data),
    };

    return trackers[type]();
  };
  return (
    <GetLifeTrackerContext.Provider
      value={{ handleTrackerQuestion, identifyFS }}
    >
      {children}
    </GetLifeTrackerContext.Provider>
  );
};
